import { GlobalStore } from '@roc/feature-app-core';
import { isNil, parseDate } from '@roc/feature-utils';
import { makeObservable, flow, action, observable, computed, override } from 'mobx';
import { PrivatePlaidService } from '../../../../apps/client-portal-public/src/app/services/privatePlaidService';
import { ACHFormSteps } from '../utils/constants';
import { getApiErrorMessage } from '../utils/achFormUtils';
import { BankInformationFormStore } from './bankInformationFormStore';

/** vendor-service AchLinkStatusResponse: the loan's newest ACH link, without its URL. Times are ISO-8601. */
export interface AchLinkStatus {
  status: 'ACTIVE' | 'LOCKED' | 'COMPLETED' | 'EXPIRED' | 'REVOKED';
  borrowerId?: number;
  createdDate?: string;
  expiresAt?: string;
  lastOpenedAt?: string;
  completedAt?: string;
  /** Open and copyable again (Copy link) without replacing it. */
  canCopy?: boolean;
  /** What the link offers the borrower: Plaid (connect the bank) or the manual form. Absent on links made before modes. */
  entryMode?: 'PLAID' | 'MANUAL';
}

enum Steps {
  BANK_INFORMATION = 0,
  DOCUSIGN = 1,
  SUMMARY = 2,
}

export abstract class AchFormBaseStore {
  public globalStore: GlobalStore;
  protected plaidService: PrivatePlaidService;

  bankInformationFormStore: BankInformationFormStore;

  public activeStep: number;
  public finalVerificationStep: number;
  public linkToken: string;
  public achData: any;
  /** Only set right after this dialog created a link: a link can't be shown again later. */
  public achUrl: string;
  /** The loan's newest link, or null if none was created (lender ACH dialog). */
  public achLinkStatus: AchLinkStatus = null;
  /** A link is being created or emailed, so the dialog's buttons don't send a second request. */
  public issuingLink = false;
  /** The loan's ACH data is being reset. */
  public resettingData = false;
  public selectedBorrower: any;
  public borrowerOptions: any;
  public currentAuthSignatoryBorrower: any;
  /** What fetchACHData was last called with (the borrower portal's encoded loan id), to reload after an update. */
  public encodedInput: string = null;
  /** A message shown above the bank step (e.g. why the borrower was moved from Plaid to the manual form). */
  public bankStepNotice: string = null;

  abstract getACHData(encodedInput: string);

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.activeStep = 0;
    this.bankInformationFormStore = new BankInformationFormStore(globalStore);
    this.selectedBorrower = null;
    this.borrowerOptions = [];
    this.currentAuthSignatoryBorrower = null;
    makeObservable(this, {
      moveToStep: observable,
      activeStep: observable,
      reset: action,
      getACHData: flow,
      fetchACHData: flow,
      setWithdrawalDay: action,
      linkToken: observable,
      achData: observable,
      getACHLink: flow,
      getManualACHLink: flow,
      achUrl: observable,
      achLinkStatus: observable,
      issuingLink: observable,
      fetchACHLinkStatus: flow,
      copyACHLink: flow,
      hasOpenLink: computed,
      canCreateManualLink: computed,
      sendACHEmail: flow,
      resetACHData: flow,
      resettingData: observable,
      finalVerificationStep: observable,
      moveToDocusignStep: flow,
      generateDocument: flow,
      selectedBorrower: observable,
      setSelectedBorrower: action,
      borrowerOptions: observable,
      generateBorrowerOptions: action,
      getLoanACHData: flow,
      disableStartACHForm: computed,
      currentAuthSignatoryBorrower: observable,
      setCurrentAuthSignatoryBorrower: action,
      bankStepNotice: observable,
      usePlaidStep: computed,
      canSwitchToPlaid: computed,
      switchToPlaid: action,
    });
  }

  /** The bank step connects through Plaid instead of the manual form. Only the public link flow offers it. */
  get usePlaidStep(): boolean {
    return false;
  }

  /** The manual form may offer "Connect your bank instead". */
  get canSwitchToPlaid(): boolean {
    return false;
  }

  public switchToPlaid() {
    // Manual entry only outside the public link flow.
  }

  public reset() {
    this.activeStep = 0;
    this.achUrl = null;
    this.achLinkStatus = null;
    this.issuingLink = false;
    this.resettingData = false;
    this.borrowerOptions = [];
    this.selectedBorrower = null;
    this.currentAuthSignatoryBorrower = null;
  }

  public *fetchACHData(encodedInput) {
    this.encodedInput = encodedInput;
    try {
      const response = yield this.getACHData(encodedInput);
      this.achData = response.data;
      this.setVerificationStep();
      this.setFinalVerificationStep();
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: getApiErrorMessage(e, `Error while retrieving ACH Data`),
      });
    }
  }

  public *moveToStep(step) {
    this.activeStep = step;
  }

  public setWithdrawalDay(param) {
    this.achData.withdrawalDay = param;
  }

  public *fetchACHLinkStatus(loanId: any) {
    try {
      const response = yield this.plaidService.getACHLinkStatus(loanId);
      this.achLinkStatus = response.data.data ?? null;
    } catch (e) {
      this.achLinkStatus = null;
    }
  }

  /** Link is open (ACTIVE or LOCKED): creating or emailing a new one makes it stop working. */
  get hasOpenLink() {
    return this.achLinkStatus?.status === 'ACTIVE' || this.achLinkStatus?.status === 'LOCKED';
  }

  /**
   * Copies the open link again, without replacing it. The clipboard write starts synchronously, before the first
   * yield, so browsers that require it to happen during the click (Safari) still allow it while the URL loads.
   */
  public *copyACHLink(loanId: any) {
    if (this.issuingLink) return;
    this.issuingLink = true;
    const urlPromise: Promise<string> = this.plaidService.copyACHLink(loanId).then(response => response.data.data);
    try {
      // TypeScript 4.1's DOM types predate ClipboardItem and clipboard.write; browsers have them.
      const ClipboardItemCtor = (window as any).ClipboardItem;
      const clipboard = navigator.clipboard as Clipboard & { write?: (items: unknown[]) => Promise<void> };
      if (ClipboardItemCtor && clipboard && clipboard.write) {
        yield clipboard.write([
          new ClipboardItemCtor({ 'text/plain': urlPromise.then(link => new Blob([link], { type: 'text/plain' })) }),
        ]);
      } else {
        const link: string = yield urlPromise;
        yield navigator.clipboard.writeText(link);
      }
      this.globalStore.notificationStore.showSuccessNotification({ message: 'Link copied to clipboard' });
    } catch (error) {
      try {
        // The URL arrived but the browser refused the clipboard: show it so it can be copied by hand.
        this.achUrl = yield urlPromise;
        this.globalStore.notificationStore.showInfoNotification({ message: "Couldn't copy automatically. Copy the link below." });
      } catch (apiError) {
        this.globalStore.notificationStore.showErrorNotification({
          message: apiError?.error?.response?.data?.error?.message ?? 'An error has occurred copying the ACH link',
        });
        yield this.fetchACHLinkStatus(loanId);
      }
    } finally {
      this.issuingLink = false;
    }
  }

  /** Creates a new link for the selected borrower; any earlier link stops working. */
  public *getACHLink(loanId: any) {
    if (this.issuingLink) return;
    this.issuingLink = true;
    try {
      const response = yield this.plaidService.getACHLink(loanId, this.selectedBorrower);
      this.achUrl = response.data.data;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: `An error has occurred creating the ACH link`,
      });
    } finally {
      this.issuingLink = false;
    }
    yield this.fetchACHLinkStatus(loanId);
  }

  /**
   * Creates a new link whose bank step is the manual form only, for the selected borrower; any earlier link stops
   * working. For when connecting the bank (Plaid) doesn't work for the borrower.
   */
  public *getManualACHLink(loanId: any) {
    if (this.issuingLink) return;
    this.issuingLink = true;
    try {
      const response = yield this.plaidService.getManualACHLink(loanId, this.selectedBorrower);
      this.achUrl = response.data.data;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: `An error has occurred creating the manual ACH link`,
      });
    } finally {
      this.issuingLink = false;
    }
    yield this.fetchACHLinkStatus(loanId);
  }

  /**
   * A manual-entry link is the fallback for when connecting the bank doesn't work, so it is offered only once a link was
   * created for this round. A link cancelled by the data reset doesn't count: after a reset the loan starts again.
   */
  get canCreateManualLink() {
    return this.achLinkStatus != null && this.achLinkStatus.status !== 'REVOKED';
  }

  /**
   * Emails a new link to the selected borrower; any earlier link stops working. The link has the Plaid connect, or only
   * the manual bank form when `manual` is true.
   */
  public *sendACHEmail(loanId: any, manual = false) {
    if (this.issuingLink) return;
    this.issuingLink = true;
    try {
      yield this.plaidService.sendACHEmail(loanId, this.selectedBorrower, manual ? 'MANUAL' : 'PLAID');
      this.achUrl = null;
      this.globalStore.notificationStore.showSuccessNotification({
        message: `Email has been sent to the borrower`,
      });
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: error?.error?.response?.data?.error?.message ?? 'An error has occurred sending the ACH link',
      });
    } finally {
      this.issuingLink = false;
    }
    yield this.fetchACHLinkStatus(loanId);
  }

  /**
   * Resets the loan's ACH data (the linked Plaid account, the ACH row, its links and the ACH Consent Form task) so the
   * form can be done again. The response says which clean-ups did not finish.
   */
  public *resetACHData(loanId: any) {
    if (this.resettingData) return;
    this.resettingData = true;
    try {
      const response = yield this.plaidService.resetACHData(loanId);
      this.achUrl = null;
      const followUps = response?.data?.data?.followUps ?? {};
      const failed = Object.keys(followUps).filter(step => followUps[step] === 'failed');
      if (failed.length > 0) {
        this.globalStore.notificationStore.showWarningNotification({
          message: `The ACH data was reset, but these steps did not finish: ${failed.join(', ')}. Run the reset again.`,
        });
      } else {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'The ACH data was reset. Close and reopen the task to see its new status.',
        });
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: getApiErrorMessage(error, 'An error has occurred resetting the ACH data'),
      });
    } finally {
      this.resettingData = false;
    }
    yield this.fetchACHLinkStatus(loanId);
  }

  public setVerificationStep() {
    switch (this.achData.verificationStep) {
      // case ACHFormSteps.SIGNATURE: this.activeStep = 1; break;
      case ACHFormSteps.SIGNED:
        this.activeStep = 2;
        break;
      default:
        this.activeStep = 0;
    }
  }

  private setFinalVerificationStep() {
    if (
      this.achData?.verificationStatus == 'pending_manual_verification' &&
      this.achData?.microdepositPosted
    ) {
      this.finalVerificationStep = 2;
    } else {
      this.finalVerificationStep = 1;
    }
  }

  *moveToDocusignStep() {
    try {
      this.bankInformationFormStore.runFormValidationWithMessage();
      if (this.bankInformationFormStore.form.meta.isValid) {
        const values = this.bankInformationFormStore.getFormValues();

        // Every non-digit, not just the first one.
        const routingNumber = values.routingNumber.replace(/[^0-9]/g, '');
        const accountNumber = values.accountNumber.replace(/[^0-9]/g, '');
        yield this.plaidService.saveBankInformation({
          ...values,
          loanId: this.achData.loanId,
          routingNumber,
          accountNumber,
          withdrawalDay: values.withdrawalDay,
        });

        yield this.plaidService.sentBofaValidation(accountNumber, routingNumber, this.achData.loanId);
        this.activeStep = Steps.DOCUSIGN;
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while saving the bank information',
      });
    }
  }

  *generateDocument(signature): Generator<unknown, void, unknown> {
    try {
      const data = {
        loanId: this.achData.loanId,
        signature: signature,
        userAgent: navigator.userAgent,
      }
      const signatureResponse = yield this.plaidService.generateAchDocument(data);
      this.activeStep = Steps.SUMMARY;
    }
    catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while generating ach document.',
      });
    }

  }

  public setSelectedBorrower(borrower) {
    this.selectedBorrower = borrower;
  }

  public setCurrentAuthSignatoryBorrower(currentBorrower) {
    this.currentAuthSignatoryBorrower = currentBorrower;
  }

  /** Staff ACH panel: only the saved borrower is needed, so the numberless summary is used (never account numbers). */
  public *getLoanACHData(loanId: any, loan) {
    const borrowerId = this.generateBorrowerOptions(loan);
    this.plaidService
      .getLoanACHSummary(loanId)
      .then(response =>
        this.setSelectedBorrower(
          isNil(borrowerId) ? response.data.data?.borrowerId : borrowerId
        )
      )
      .catch(error =>
        this.globalStore.notificationStore.showErrorNotification({
          message: `An error has occurred getting the ACH data`,
        })
      );
  }

  public generateBorrowerOptions(loan: any) {
    const options = loan.borrowers?.filter(borrower => borrower.authSignatory);
    if (options.length === 1) {
      this.setCurrentAuthSignatoryBorrower(options[0]);
      this.borrowerOptions = options;
      return options[0].borrowerId;
    } else if (options.length > 1) {
      const borrowers = [
        { label: 'Please select a borrower', value: null, id: null },
      ];
      options?.forEach(element => {
        borrowers.push({
          label: element.fullName,
          value: element.borrowerId,
          id: element.borrowerId,
        });
      });
      this.borrowerOptions = borrowers;
    }
    return null;
  }

  get disableStartACHForm() {
    return !this.achData || !this.achData?.borrowerId;
  }
}

/**
 * The ACH form store for signed-in users: the lender portal (get and email the borrower's link) and the borrower portal
 * (fill in the form for a loan). The public page (/link/ach) uses SecureLinkAchFormStore.
 */
export class AchFormStore extends AchFormBaseStore {
  *getACHData(encodedInput) {
    return this.plaidService.getACHDataForBorrowerPortal(encodedInput);
  }

  constructor(globalStore: GlobalStore) {
    super(globalStore);
    this.plaidService = new PrivatePlaidService();
    makeObservable(this, {
      getACHData: override,
    });
  }
}
